import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

// Firebase Functions 초기화
const functions = getFunctions(getApp(), 'us-central1');

/**
 * 숏폼 퀴즈 누가기록(학습 평가 기록) 생성 서비스
 * 
 * 학생의 숏폼 퀴즈 결과를 기반으로 OpenAI를 활용하여
 * 전문적인 누가기록을 생성합니다.
 */
class ShortFormCumulativeRecordGenerator {
  
  /**
   * 누가기록 생성 요청
   * 
   * @param {Object} params - 생성 요청 파라미터
   * @param {Object} params.quizData - 숏폼 퀴즈 데이터
   * @param {string} params.quizData.quizTitle - 퀴즈 제목
   * @param {string} params.quizData.quizDescription - 퀴즈 설명 (선택)
   * @param {number} params.quizData.score - 점수
   * @param {number} params.quizData.correctCount - 맞춘 문제 수
   * @param {number} params.quizData.totalQuestions - 전체 문제 수
   * @param {Array<Object>} params.quizData.correctQuestions - 맞춘 문제들
   * @param {string} params.quizData.correctQuestions[].content - 문제 내용
   * @param {string} params.quizData.correctQuestions[].category - 문제 카테고리
   * @param {string} params.quizData.correctQuestions[].difficulty - 난이도
   * @param {Object} params.studentInfo - 학생 정보
   * @param {string} params.studentInfo.name - 학생 이름
   * @param {string} params.studentInfo.class - 학급명 (선택)
   * @param {string} params.userId - 사용자 ID
   * @param {boolean} params.isProUser - 프로 사용자 여부 (기본: false)
   * 
   * @returns {Promise<Object>} 생성된 누가기록 결과
   * @returns {boolean} result.success - 성공 여부
   * @returns {string} result.record - 생성된 누가기록 텍스트
   * @returns {Object} result.metadata - 생성 메타데이터
   * @returns {string} result.error - 오류 메시지 (실패 시)
   * 
   * @example
   * const result = await shortFormCumulativeRecordGenerator.generateRecord({
   *   quizData: {
   *     quizTitle: '우리 반 선생님 알아맞히기',
   *     quizDescription: '선생님의 특성과 습관 퀴즈',
   *     score: 85,
   *     correctCount: 17,
   *     totalQuestions: 20,
   *     correctQuestions: [
   *       { content: '선생님이 가장 좋아하는 취미는?', category: '선생님', difficulty: 'easy' },
   *       { content: '선생님의 말버릇은?', category: '선생님', difficulty: 'medium' }
   *     ]
   *   },
   *   studentInfo: {
   *     name: '홍길동',
   *     class: '1학년 3반'
   *   },
   *   userId: 'teacher123',
   *   isProUser: false
   * });
   */
  async generateRecord({
    quizData,
    studentInfo = {},
    userId = 'anonymous',
    isProUser = false
  }) {
    try {
      // 파라미터 유효성 검사
      if (!quizData) {
        throw new Error('숏폼 퀴즈 데이터가 필요합니다.');
      }

      if (!quizData.quizTitle) {
        throw new Error('퀴즈 제목이 필요합니다.');
      }

      console.log('🚀 숏폼 퀴즈 누가기록 생성 요청:', {
        quizTitle: quizData.quizTitle,
        score: quizData.score,
        correctCount: quizData.correctCount,
        totalQuestions: quizData.totalQuestions,
        studentName: studentInfo.name,
        userId,
        isProUser
      });

      // Firebase Function 호출
      const generateShortFormCumulativeRecord = httpsCallable(functions, 'generateShortFormCumulativeRecord');
      
      console.log('📡 Firebase Function 호출 중...');
      
      const response = await generateShortFormCumulativeRecord({
        quizData,
        studentInfo,
        userId,
        isProUser
      });

      console.log('📥 누가기록 생성 API 응답:', {
        hasData: !!response?.data,
        success: response?.data?.success,
        hasRecord: !!response?.data?.record,
        status: response?.data?.status,
        fullResponse: response.data
      });

      // 🔥 API 응답이 없거나 비정상인 경우 처리
      if (!response || !response.data) {
        throw new Error('API 응답이 없습니다.');
      }

      const result = response.data;

      // 🔥 placeholder 응답 감지 (Firebase Function이 구현되지 않음)
      if (result.status === 'placeholder') {
        throw new Error('⚠️ Firebase Function이 아직 구현되지 않았습니다.\n\nfunctions/index.js에 generateShortFormCumulativeRecord 함수를 구현하고 배포해주세요.\n\n배포 명령어: firebase deploy --only functions');
      }

      // 🔥 success가 false인 경우
      if (result.success === false) {
        throw new Error(result.error || result.message || '누가기록 생성에 실패했습니다.');
      }

      // 🔥 record가 없는 경우
      if (!result.record || result.record.trim().length === 0) {
        throw new Error('생성된 누가기록 내용이 없습니다. Firebase Function을 확인해주세요.');
      }

      console.log('✅ 누가기록 생성 완료:', {
        success: result.success,
        recordLength: result.record?.length
      });

      return result;

    } catch (error) {
      console.error('누가기록 생성 실패:', error);
      
      // 에러 메시지 처리
      let errorMessage = '누가기록 생성 중 오류가 발생했습니다.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.code === 'unauthenticated') {
        errorMessage = '로그인이 필요합니다.';
      } else if (error.code === 'permission-denied') {
        errorMessage = '권한이 없습니다.';
      } else if (error.code === 'resource-exhausted') {
        errorMessage = 'API 사용량을 초과했습니다. 잠시 후 다시 시도해주세요.';
      }

      return {
        success: false,
        error: errorMessage,
        record: '',
        metadata: {}
      };
    }
  }

  /**
   * 여러 학생의 누가기록 일괄 생성
   * 
   * @param {Object} params - 일괄 생성 요청 파라미터
   * @param {Array<Object>} params.students - 학생 배열
   * @param {string} params.students[].id - 학생 ID
   * @param {string} params.students[].name - 학생 이름
   * @param {Object} params.students[].quizData - 각 학생의 퀴즈 데이터
   * @param {string} params.userId - 사용자 ID
   * @param {boolean} params.isProUser - 프로 사용자 여부
   * @param {Function} params.onProgress - 진행 상황 콜백 함수 (선택)
   * 
   * @returns {Promise<Array<Object>>} 생성 결과 배열
   */
  async generateBulkRecords({
    students = [],
    userId = 'anonymous',
    isProUser = false,
    onProgress = null
  }) {
    console.log('🚀 숏폼 퀴즈 누가기록 일괄 생성 시작:', {
      studentCount: students.length,
      userId,
      isProUser
    });

    if (!students || students.length === 0) {
      console.error('❌ 생성할 학생이 없습니다.');
      return [];
    }

    const results = [];
    let completedCount = 0;

    for (const student of students) {
      try {
        console.log(`\n📝 [${completedCount + 1}/${students.length}] ${student.name} 누가기록 생성 중...`);
        
        // 진행 상황 콜백 호출
        if (onProgress) {
          onProgress({
            completed: completedCount,
            total: students.length,
            current: student,
            waiting: students.slice(completedCount + 1)
          });
        }

        // 실제 누가기록 생성 호출
        const result = await this.generateRecord({
          quizData: student.quizData,
          studentInfo: {
            name: student.name,
            class: student.class
          },
          userId,
          isProUser
        });

        console.log(`   ✨ 생성 완료:`, {
          success: result?.success,
          hasRecord: !!result?.record,
          error: result?.error
        });

        // 결과 저장
        results.push({
          studentId: student.id,
          studentName: student.name,
          success: result.success,
          record: result.record || '',
          metadata: result.metadata || {},
          error: result.error || null
        });

        completedCount++;

      } catch (error) {
        console.error(`   ❌ ${student.name} 누가기록 생성 실패:`, error);
        
        results.push({
          studentId: student.id,
          studentName: student.name,
          success: false,
          record: '',
          metadata: {},
          error: error.message || '알 수 없는 오류가 발생했습니다.'
        });

        completedCount++;
      }
    }

    // 최종 진행 상황 콜백
    if (onProgress) {
      onProgress({
        completed: completedCount,
        total: students.length,
        current: null,
        waiting: []
      });
    }

    console.log('\n🎉 누가기록 일괄 생성 완료:', {
      total: students.length,
      success: results.filter(r => r.success).length,
      failed: results.filter(r => !r.success).length
    });

    return results;
  }

  /**
   * 생성 가능 여부 확인
   * 
   * @param {Object} quizData - 퀴즈 데이터
   * @returns {Object} 검증 결과
   */
  validateQuizData(quizData) {
    const errors = [];
    const warnings = [];

    if (!quizData) {
      errors.push('퀴즈 데이터가 없습니다.');
      return { valid: false, errors, warnings };
    }

    // 필수 데이터 확인
    if (!quizData.quizTitle || quizData.quizTitle.trim().length === 0) {
      errors.push('퀴즈 제목이 필요합니다.');
    }

    if (quizData.score === undefined || quizData.score === null) {
      warnings.push('점수 정보가 없습니다.');
    }

    if (!quizData.correctCount || quizData.correctCount === 0) {
      warnings.push('맞춘 문제가 없습니다. 누가기록 내용이 제한적일 수 있습니다.');
    }

    if (!quizData.correctQuestions || quizData.correctQuestions.length === 0) {
      warnings.push('맞춘 문제의 상세 정보가 없습니다. 더 구체적인 누가기록을 위해 문제 정보를 추가하는 것을 권장합니다.');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }
}

// 싱글톤 인스턴스 생성 및 export
export const shortFormCumulativeRecordGenerator = new ShortFormCumulativeRecordGenerator();
export default ShortFormCumulativeRecordGenerator;

